import { useCallback, useEffect, useRef, useState } from 'react';

type StartProgressConfig = {
  /**
   * Duration for pseudo progress animation in milliseconds
   */
  duration?: number;
  /**
   * Whether to use pseudo progress animation
   */
  pseudo?: boolean;
  /**
   * Initial progress value (0-100)
   */
  initial?: number;
  /**
   * Minimum pseudo progress value (0-100)
   */
  min?: number;
  /**
   * Maximum pseudo progress value (0-100)
   */
  max?: number;
};

type StartProgressInput = number | StartProgressConfig;

type UseProgressReturn = {
  /**
   * Current progress value (0-100)
   */
  progress: number;
  /**
   * Whether the progress is currently active
   */
  isActive: boolean;
  /**
   * Whether the progress is in pseudo mode
   */
  isPseudo: boolean;
  /**
   * Starts the progress
   */
  startProgress: (input?: StartProgressInput) => void;
  /**
   * Updates the progress to a specific value
   */
  updateProgress: (value: number) => void;
  /**
   * Finishes the progress
   */
  finishProgress: () => void;
};

const DEFAULT_PSEUDO_DURATION = 5000;
const MIN_PSEUDO_PROGRESS = 5;
const PSEUDO_MAX_PROGRESS = 95;
const COMPLETION_DELAY = 500;
const EPSILON = 0.05;

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));
const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

const normalizeStartOptions = (input?: StartProgressInput): StartProgressConfig => {
  if (typeof input === 'number') {
    return { duration: input };
  }

  return input ?? {};
};

export const useProgress = (): UseProgressReturn => {
  const [progress, setProgress] = useState(0);
  const [isActive, setIsActive] = useState(false);
  const [isPseudo, setIsPseudo] = useState(false);

  const progressRef = useRef(0);
  const durationRef = useRef(DEFAULT_PSEUDO_DURATION);
  const minPseudoProgressRef = useRef(MIN_PSEUDO_PROGRESS);
  const maxPseudoProgressRef = useRef(PSEUDO_MAX_PROGRESS);
  const startTimeRef = useRef<number | null>(null);
  const pseudoAnimationHandleRef = useRef<number | null>(null);
  const pseudoActiveRef = useRef(false);
  const pseudoStepRef = useRef<(timestamp: number) => void>(() => {});
  const isPseudoRef = useRef(false);
  const completionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const finishedRef = useRef(false);

  const clearCompletionTimer = useCallback(() => {
    if (completionTimerRef.current) {
      clearTimeout(completionTimerRef.current);
      completionTimerRef.current = null;
    }
  }, []);

  const cancelPseudoAnimation = useCallback(() => {
    if (typeof window !== 'undefined' && pseudoAnimationHandleRef.current !== null) {
      window.cancelAnimationFrame(pseudoAnimationHandleRef.current);
      pseudoAnimationHandleRef.current = null;
    }

    pseudoActiveRef.current = false;
    startTimeRef.current = null;
  }, []);

  useEffect(() => {
    progressRef.current = progress;
  }, [progress]);

  const schedulePseudoStep = useCallback(() => {
    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      pseudoAnimationHandleRef.current = window.requestAnimationFrame((timestamp) => pseudoStepRef.current(timestamp));
    }
  }, []);

  const pseudoStep = useCallback(
    (timestamp: number) => {
      if (!pseudoActiveRef.current || finishedRef.current) {
        return;
      }

      if (typeof window === 'undefined') {
        pseudoActiveRef.current = false;
        pseudoAnimationHandleRef.current = null;
        return;
      }

      if (startTimeRef.current === null) {
        startTimeRef.current = timestamp;
      }

      const duration = Math.max(durationRef.current, 500);
      const elapsed = timestamp - startTimeRef.current;
      const normalized = clamp(elapsed / duration, 0, 1);
      const eased = easeOutCubic(normalized);
      const baseline = Math.max(progressRef.current, minPseudoProgressRef.current);
      const maxPseudoProgress = maxPseudoProgressRef.current;
      const target = clamp(eased * maxPseudoProgress, baseline, maxPseudoProgress);

      if (target - progressRef.current > EPSILON) {
        progressRef.current = target;
        setProgress(target);
      }

      if (normalized < 1 && progressRef.current < maxPseudoProgress - EPSILON) {
        schedulePseudoStep();
      } else {
        pseudoActiveRef.current = false;
        pseudoAnimationHandleRef.current = null;
      }
    },
    [schedulePseudoStep]
  );

  useEffect(() => {
    pseudoStepRef.current = pseudoStep;
  }, [pseudoStep]);

  useEffect(() => {
    return () => {
      clearCompletionTimer();
      cancelPseudoAnimation();
    };
  }, [clearCompletionTimer, cancelPseudoAnimation]);

  const finishProgress = useCallback(() => {
    if (finishedRef.current) {
      return;
    }

    finishedRef.current = true;
    cancelPseudoAnimation();
    isPseudoRef.current = false;
    setIsPseudo(false);
    const completeValue = 100;
    progressRef.current = completeValue;
    setProgress(completeValue);

    if (typeof window === 'undefined') {
      setIsActive(false);
      return;
    }

    clearCompletionTimer();
    completionTimerRef.current = setTimeout(() => {
      setIsActive(false);
      completionTimerRef.current = null;
    }, COMPLETION_DELAY);
  }, [cancelPseudoAnimation, clearCompletionTimer]);

  const startProgress = useCallback(
    (input?: StartProgressInput) => {
      const options = normalizeStartOptions(input);

      clearCompletionTimer();
      cancelPseudoAnimation();
      finishedRef.current = false;

      const normalizedInitial = clamp(options.initial ?? 0, 0, 100);
      const pseudoRequested = Boolean(options.pseudo);

      progressRef.current = normalizedInitial;
      setProgress(normalizedInitial);
      setIsActive(true);

      isPseudoRef.current = pseudoRequested;
      setIsPseudo(pseudoRequested);

      durationRef.current = options.duration && options.duration > 0 ? options.duration : DEFAULT_PSEUDO_DURATION;
      maxPseudoProgressRef.current = clamp(options.max ?? PSEUDO_MAX_PROGRESS, 0, 100);
      minPseudoProgressRef.current = clamp(options.min ?? MIN_PSEUDO_PROGRESS, 0, maxPseudoProgressRef.current);

      if (pseudoRequested && typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
        pseudoActiveRef.current = true;
        startTimeRef.current = null;

        const initialProgress = Math.max(normalizedInitial, minPseudoProgressRef.current);
        if (initialProgress - normalizedInitial > EPSILON) {
          progressRef.current = initialProgress;
          setProgress(initialProgress);
        }

        schedulePseudoStep();
      }
    },
    [cancelPseudoAnimation, clearCompletionTimer, schedulePseudoStep]
  );

  const updateProgress = useCallback(
    (value: number) => {
      const nextValue = clamp(value, 0, 100);

      clearCompletionTimer();
      finishedRef.current = false;

      if (isPseudoRef.current) {
        cancelPseudoAnimation();
        isPseudoRef.current = false;
        setIsPseudo(false);
      }

      progressRef.current = nextValue;
      setProgress(nextValue);

      if (nextValue >= 100) {
        finishProgress();
      } else if (nextValue > 0) {
        setIsActive(true);
      }
    },
    [cancelPseudoAnimation, clearCompletionTimer, finishProgress]
  );

  return {
    progress,
    isActive,
    isPseudo,
    startProgress,
    updateProgress,
    finishProgress
  };
};
